import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Security,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Login,
  AdminPanelSettings,
  People,
  Search,
  Schedule,
  Refresh
} from '@mui/icons-material';
import { supabase } from '../../services/supabaseClient';
import axios from 'axios';

const SecurityAudit = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const securityChecks = [
    {
      id: 1,
      check: 'Row Level Security (RLS)',
      status: 'success',
      message: 'Enabled on all user data tables',
      recommendation: 'Audit RLS policies quarterly'
    },
    {
      id: 2,
      check: 'Service Role Key',
      status: 'success',
      message: 'Only used in backend API routes, never exposed to client',
      recommendation: 'Rotate keys periodically via Supabase dashboard'
    },
    {
      id: 3,
      check: 'Admin Access Control',
      status: 'success',
      message: 'Admin routes protected via ProtectedRoute + isAdmin check',
      recommendation: 'Keep admin user count minimal'
    },
    {
      id: 4,
      check: 'API Rate Limiting',
      status: 'warning',
      message: 'Backend enforces daily word caps per tier but no global rate limit',
      recommendation: 'Consider adding per-IP rate limiting for translation API'
    },
    {
      id: 5,
      check: 'Audit Logging',
      status: 'warning',
      message: 'No dedicated audit_logs table yet',
      recommendation: 'Create an audit_logs table to track admin actions and security events'
    }
  ];

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const response = await axios.get('/api/admin/users', {
        params: { perPage: 100 },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'success': return 'success';
      default: return 'info';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error': return <ErrorIcon color="error" />;
      case 'warning': return <Warning color="warning" />;
      case 'success': return <CheckCircle color="success" />;
      default: return <CheckCircle color="info" />;
    }
  };

  // Derive metrics from real user data
  const totalUsers = users.length;
  const adminUsers = users.filter((u) => u.app_metadata?.isAdmin === true || u.user_metadata?.isAdmin === true);
  const recentLogins = users
    .filter((u) => u.last_sign_in_at)
    .sort((a, b) => new Date(b.last_sign_in_at) - new Date(a.last_sign_in_at));
  const unverifiedUsers = users.filter((u) => !u.email_confirmed_at);
  const suspendedUsers = users.filter((u) => u.banned_until);

  const filteredLogins = searchTerm
    ? recentLogins.filter((u) =>
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.id || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : recentLogins;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          Security & Audit
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      {/* Security Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <People color="primary" />
                <Typography variant="h6" ml={1}>Total Users</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {totalUsers}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Registered accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AdminPanelSettings color="warning" />
                <Typography variant="h6" ml={1}>Admins</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {adminUsers.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {adminUsers.map((u) => u.email).join(', ') || 'None'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Warning color="error" />
                <Typography variant="h6" ml={1}>Unverified</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {unverifiedUsers.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Email not confirmed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Security color="error" />
                <Typography variant="h6" ml={1}>Suspended</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {suspendedUsers.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Banned accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Security Health Checks */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Security Health Checks
              </Typography>
              <List>
                {securityChecks.map((check, index) => (
                  <React.Fragment key={check.id}>
                    <ListItem>
                      <ListItemIcon>
                        {getSeverityIcon(check.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1" fontWeight="medium">
                              {check.check}
                            </Typography>
                            <Chip
                              label={check.status.toUpperCase()}
                              color={getSeverityColor(check.status)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {check.message}
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                              Recommendation: {check.recommendation}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < securityChecks.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent User Sign-Ins */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Recent Sign-Ins
            </Typography>
            <TextField
              size="small"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading && users.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Sign-In</TableCell>
                    <TableCell>Account Created</TableCell>
                    <TableCell>Email Verified</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogins.slice(0, 50).map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {(user.app_metadata?.isAdmin || user.user_metadata?.isAdmin) ? (
                            <AdminPanelSettings sx={{ mr: 1, fontSize: 18, color: 'warning.main' }} />
                          ) : (
                            <Login sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.email}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.id.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            user.banned_until ? 'Suspended' :
                            (user.app_metadata?.isAdmin || user.user_metadata?.isAdmin) ? 'Admin' :
                            !user.email_confirmed_at ? 'Unverified' : 'Active'
                          }
                          color={
                            user.banned_until ? 'error' :
                            (user.app_metadata?.isAdmin || user.user_metadata?.isAdmin) ? 'warning' :
                            !user.email_confirmed_at ? 'default' : 'success'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Schedule sx={{ mr: 0.5, fontSize: 14 }} />
                          <Typography variant="body2">
                            {user.last_sign_in_at
                              ? new Date(user.last_sign_in_at).toLocaleString()
                              : 'Never'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(user.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.email_confirmed_at ? 'Yes' : 'No'}
                          color={user.email_confirmed_at ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SecurityAudit;
